import React, { useState, useEffect, useCallback } from 'react';
import { SERVER_URL } from '../../../constants.js'
import AddStay from '../../AddsEdits/Student/AddStay.js';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import EditStay from '../../AddsEdits/Student/EditStay.js';
import IconButton from '@mui/material/IconButton';
import LinkAgentDialog from '../../Agents/LinkAgentDialog.jsx';
import { addStudentStay } from '../../shared utils/apiUtils.js';
import { isAdmin } from '../../shared utils/auth.js';
import { formatLocalDateOnly } from '../../shared utils/dateFormatters';
import { jsonOr } from "../../../utils/http";

function StudentStays(props) {
    const { selectedPerson, selectedStay, setSelectedStay } = props;
    const [stays, setStays] = useState([]);
    const [editOpen, setEditOpen] = useState(false);
    const [campuses, setCampuses] = useState([]);
    // The stay we're linking to an agent — held separately from selectedStay so
    // opening the dialog doesn't change which stay bookings are added to.
    const [agentStay, setAgentStay] = useState(null);
    // Everyone sees which agent a stay belongs to — the backend returns
    // agentId/agentName to USER as well. Only ADMIN can change it, in line with
    // the rest of the agent module, and only ADMIN is sent the commission.
    const canEditAgent = isAdmin();

    useEffect(() => {
        const token = sessionStorage.getItem("bearer");
        fetch(`${SERVER_URL}api/campuses/active`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then((res) => jsonOr(res, []))
            .then((data) => setCampuses(data))
            .catch((err) => console.error("Failed to fetch campuses:", err));
    }, []);

    const fetchStays = useCallback(() => {
        const token = sessionStorage.getItem('bearer');
        fetch(`${SERVER_URL}api/student/${selectedPerson.id}/stays`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((response) => {
                if (response.status === 204) {
                    return [];
                } else if (response.ok) {
                    return response.json();
                } else {
                    throw new Error('Failed to fetch stays');
                }
            })
            .then((data) => {
                sessionStorage.setItem('stays', JSON.stringify(data));
                setStays(data);
            })
            .catch((err) => console.error(err));
    }, [selectedPerson.id]);

    useEffect(() => {
        fetchStays();
    }, [fetchStays]);

    useEffect(() => {
        if (!stays || stays.length === 0) {
            if (selectedStay) {
                setSelectedStay(null);
            }
            return;
        }

        if (!selectedStay) {
            setSelectedStay(stays[stays.length - 1]);
            return;
        }

        const stillExists = stays.some((stay) => stay.stayId === selectedStay.stayId);
        if (!stillExists) {
            setSelectedStay(stays[stays.length - 1]);
        }
    }, [stays, selectedStay, setSelectedStay]);

    const handleStaySelect = useCallback((stay) => {
        if (selectedStay && stay.stayId === selectedStay.stayId) {
            return;
        }
        setSelectedStay(stay);
    }, [selectedStay, setSelectedStay]);

    const handleAddStay = (stay) => {
        addStudentStay(stay, selectedPerson.id, fetchStays)
            .catch(err => alert('Something went wrong!'));
    };

    const handleEditClick = (stay) => {
        setSelectedStay(stay);
        setEditOpen(true);
    };

    const editStay = (stay, studentId, stayId) => {
        // Moving campus releases the student from the old campus's campus-linked
        // groups (activities/transport/bedrooms/classes) and they need
        // re-allocating at the new one — we don't auto-enrol. Warn before saving.
        const oldCampus = selectedStay?.campus;
        const newCampus = stay?.campus;
        if (oldCampus && newCampus && oldCampus !== newCampus) {
            const ok = window.confirm(
                `Move campus?\n\n` +
                `Changing this stay from ${oldCampus} to ${newCampus} will release this ` +
                `student from ${oldCampus}'s activity, transport, bedroom and class groups. ` +
                `You'll need to re-allocate them at ${newCampus} — we don't auto-enrol.\n\n` +
                `Continue?`
            );
            if (!ok) return;
        }

        const token = sessionStorage.getItem("bearer");

        const formattedStay = {
            ...stay,
            arrivalDate: formatLocalDateOnly(stay.arrivalDate),
            departureDate: formatLocalDateOnly(stay.departureDate),
        };

        fetch(`${SERVER_URL}api/student/${studentId}/stay/${stayId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(formattedStay)
        })
            .then(response => {
                if (response.ok) {
                    fetchStays();
                } else {
                    alert('Something went wrong!');
                }
            })
            .catch(err => console.error(err));
    };

    const deleteStay = (event, stay) => {
        event.preventDefault();

        // Removing a stay releases the student from that campus's campus-linked
        // groups; if they have another stay they need re-allocating there.
        const ok = window.confirm(
            `Remove this stay?\n\n` +
            `Deleting this ${stay.campus} stay will release this student from ` +
            `${stay.campus}'s activity, transport, bedroom and class groups. If they ` +
            `have another stay, re-allocate them there — we don't auto-enrol.\n\n` +
            `Continue?`
        );
        if (!ok) return;

        const token = sessionStorage.getItem("bearer");

        fetch(`${SERVER_URL}api/student/${selectedPerson.id}/stay/${stay.stayId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        })
            .then(response => {
                if (response.ok) {
                    fetchStays();
                } else {
                    alert('Something went wrong!');
                }
            })
            .catch(err => console.error(err));
    };

    return (
        <React.Fragment>
            <div className="detail-card payments-card" style={{ padding: '20px 0' }}>
                <table style={{ width: '80%', textAlign: 'left', margin: 'auto', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Campus</th>
                            <th>Arrival</th>
                            <th>Departure</th>
                            <th>Residential</th>
                            <th>Agent</th>
                            <th>Actions</th>
                            <th>Select for Booking</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stays.map((stay) => (
                            <tr key={stay.stayId}>
                                <td>{stay.stayId}</td>
                                <td>{stay.campus}</td>
                                <td>{stay.arrivalDate}</td>
                                <td>{stay.departureDate}</td>
                                <td>{stay.residential ? 'Yes' : 'No'}</td>
                                <td>
                                    {stay.agentId
                                        ? (stay.agentName || `Agent #${stay.agentId}`)
                                        : <span style={{ color: '#999999' }}>—</span>}
                                </td>
                                <td>
                                    <IconButton onClick={() => handleEditClick(stay)} color="primary" size="small">
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton onClick={(event) => deleteStay(event, stay)} color="primary" size="small">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                    {canEditAgent && (
                                        <IconButton
                                            onClick={() => setAgentStay(stay)}
                                            color="primary"
                                            size="small"
                                            title={stay.agentId
                                                ? `Change or remove the agent on this stay`
                                                : 'Link this stay to an agent'}
                                        >
                                            <LinkIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={!!selectedStay && stay.stayId === selectedStay.stayId}
                                        onChange={() => handleStaySelect(stay)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <AddStay
                passedStudent={selectedPerson}
                addStay={handleAddStay}
                campuses={campuses}
            />

            {editOpen && (
                <EditStay
                    passedStudent={selectedPerson}
                    selectedStay={selectedStay}
                    open={editOpen}
                    onClose={() => setEditOpen(false)}
                    editStay={(stay) => editStay(stay, selectedPerson.id, selectedStay.stayId)}
                    campuses={campuses}
                />
            )}

            <LinkAgentDialog
                open={!!agentStay}
                stay={agentStay}
                studentName={`${selectedPerson.studentName || ''} ${selectedPerson.studentSurname || ''}`.trim()}
                onClose={() => setAgentStay(null)}
                onSaved={fetchStays}
            />

            <div>
                <p style={{ color: '#999999', fontSize: '10px' }}>Student: {selectedPerson.id}</p>
                <p style={{ color: '#999999', fontSize: '10px' }}>Selected Stay: {selectedStay ? selectedStay.stayId : 'None'}</p>
                <p style={{ color: '#999999', fontSize: '10px' }}>Is authenticated: {sessionStorage.getItem('isAuthenticated').toString()}</p>
            </div>
        </React.Fragment>
    );
}

export default StudentStays;